import { useMutation, useQuery } from "@tanstack/react-query";
import { Clock3 } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { HUB_CLIENT_BUILD_VERSION } from "@/lib/hubBuildVersion";
import { useIntegratorMe } from "@/lib/me-query";
import {
  fetchQuickBooksTimeOAuthStatus,
  startQuickBooksTimeOAuth,
  WORKSPACE_AVATAR_SYNC_OPERATOR_EMAIL,
} from "@/lib/quickbooksTimeOAuth";
import {
  quickBooksTimeBannerMessage,
  quickBooksTimeNeedsReconnectSoon,
  quickBooksTimeOAuthBannerVisible,
} from "@/lib/quickBooksTimeMessages";
import { isHubDevInstance } from "@/lib/slowRequestSupportTicket";
import { useConfirmedQueryFailure } from "@/lib/useConfirmedQueryFailure";

function hasQuickBooksTimeCallbackMessage(search: string): boolean {
  const params = new URLSearchParams(search);
  return params.has("quickbooksTime") || params.has("quickbooksTimeError");
}

function isQuickBooksTimeOperator(email: string | null | undefined): boolean {
  return (email?.trim().toLowerCase() ?? "") === WORKSPACE_AVATAR_SYNC_OPERATOR_EMAIL;
}

export function QuickBooksTimeOAuthBanner() {
  const location = useLocation();
  const meQuery = useIntegratorMe();
  const operatorSession = isQuickBooksTimeOperator(meQuery.data?.user.email);
  const callbackMessage = hasQuickBooksTimeCallbackMessage(location.search);

  const statusQuery = useQuery({
    queryKey: ["manufacturing-reporting", "quickbooks-time-oauth-status"],
    queryFn: fetchQuickBooksTimeOAuthStatus,
    enabled: operatorSession && meQuery.isSuccess && !callbackMessage,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  const connectMutation = useMutation({
    mutationFn: () =>
      startQuickBooksTimeOAuth({
        returnTo: `${location.pathname}${location.search}${location.hash}`,
        uiOrigin: window.location.origin,
      }),
    onSuccess: (payload) => {
      window.location.assign(payload.authorizeUrl);
    },
  });

  const status = statusQuery.data;
  const statusRequestFailed = useConfirmedQueryFailure(statusQuery);
  const statusUnavailable =
    statusRequestFailed &&
    !isHubDevInstance({
      clientBuildVersion: HUB_CLIENT_BUILD_VERSION,
      viteDev: import.meta.env.DEV === true,
      hostname: typeof window === "undefined" ? "" : window.location.hostname,
    });
  const shouldShow =
    operatorSession &&
    (statusUnavailable ||
      (status?.operatorEligible && status.clientConfigured && quickBooksTimeOAuthBannerVisible(status)));

  if (!shouldShow) {
    return null;
  }

  const expiringSoon = !statusUnavailable && quickBooksTimeNeedsReconnectSoon(status);
  const bannerMessage = quickBooksTimeBannerMessage(status, statusUnavailable);
  const toneClass = expiringSoon
    ? "border-sky-200 bg-sky-50 text-sky-950"
    : "border-amber-200 bg-amber-50 text-amber-950";
  const iconClass = expiringSoon ? "text-sky-700" : "text-amber-700";
  const linkClass = expiringSoon
    ? "font-medium text-sky-900 underline decoration-sky-500 underline-offset-2 hover:text-sky-950"
    : "font-medium text-amber-900 underline decoration-amber-500 underline-offset-2 hover:text-amber-950";
  const buttonLabel = expiringSoon
    ? "Renew QuickBooks Time sign-in"
    : status?.connected
      ? "Reconnect QuickBooks Time"
      : "Connect QuickBooks Time";

  return (
    <div
      role="status"
      aria-live="polite"
      className={`relative z-[40] border-b px-4 py-2 text-sm ${toneClass}`}
    >
      <div className="mx-auto flex w-full max-w-[min(100%,2400px)] flex-wrap items-center justify-center gap-x-3 gap-y-2 px-3 sm:px-4 lg:px-6 2xl:px-10">
        <Clock3 className={`h-4 w-4 shrink-0 ${iconClass}`} aria-hidden="true" />
        <span>{bannerMessage}</span>
        <button
          type="button"
          className={linkClass}
          disabled={connectMutation.isPending}
          onClick={() => void connectMutation.mutate()}
        >
          {buttonLabel}
        </button>
        <Link
          to="/manufacturing-management/reporting"
          className={linkClass}
        >
          Open daily task report
        </Link>
      </div>
    </div>
  );
}
